import { useState, useRef, useEffect, useMemo } from 'react';
import SelectField from '../common/SelectField.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import DeliveryOtpModal from '../common/DeliveryOtpModal.js';
import shipmentQrStatusService from '../../services/shipmentQrStatusService.js';
import shipmentCrossingService from '../../services/shipmentCrossingService.js';
import CrossingVehicleSelect from '../shipment-crossing/CrossingVehicleSelect.js';
import { prepareScanImageFile, prepareQrScanImageFile, isAcceptedScanFile } from '../../utils/normalizeCameraImage.js';
import { amountBeforeDemurrage, demurrageDisplayAmount, getQrBatchStatusOptions, isCreditClientShipment, isShipmentFullyPaid, normalizeQrResolveResponse, getQrTransitionEligibility, getEligibleShipmentsForQrStatus, requiresDeliveryCollection } from '../../utils/shipmentPayment.js';
import { isOtpRequiredResponse, normalizeOtpPending } from '../../utils/deliveryOtp.js';
import { Camera, QrCode, CheckCircle, Upload, FileText, X, Shuffle, CalendarCheck } from 'lucide-react';
import LiveQrScanner, { supportsLiveQrScan } from '../common/LiveQrScanner.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { getCurrentDevicePosition } from '../../utils/deviceLocation.js';
import {
  hasShipmentCrossingAccess,
  canScanQrUpdateStatus,
  canScanQrAttendance,
} from '../../utils/networkMode.js';
import { parseHubAttendanceQr } from '../../utils/parseHubAttendanceQr.js';
import {
  parseShipmentIdFromQrData,
  parseUnitNoFromQrData,
  buildVerifiedUnitsByShipment,
  shipmentUnitProgressLabel,
  shipmentMissingUnitLabels,
  isShipmentUnitScanComplete,
} from '../../utils/parseQrShipmentId.js';
import attendanceService from '../../services/AttendanceService.js';
import { ensureCameraPermission } from '../../utils/devicePermissions.js';
import CapturePhotoField from '../common/CapturePhotoField.js';
import HubAgentPickPanel from '../ml-sheet/HubAgentPickPanel.js';
import { shipmentService } from '../../services/shipmentService.js';
import {
  shouldPickReceivingAgent,
  groupShipmentsByReceiverHub,
  buildReceiverAgentByShipment,
  validateHubAgentSelections,
} from '../../utils/receivingAgentPicker.js';

const isPdfFile = (file) =>
  file?.type === 'application/pdf' || (file?.name || '').toLowerCase().endsWith('.pdf');

const notifyScanError = (message) => {
  const msg = String(message || '').trim();
  if (!msg) return;
  if (Alert.isRouteDispatchMessage(msg)) {
    void Alert.routeDispatchBlocked(msg);
    return;
  }
  Alert.error(msg);
};

const paymentModeLabel = (shipment) => {
  const raw = String(shipment?.mode_of_payment || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
  if (isCreditClientShipment(shipment) || raw === 'credit') return 'CREDIT';
  if (raw === 'paid' || raw === 'paid balance') return 'PAID';
  if (raw === 'to pay') return 'TO PAY';
  if (!raw) return requiresDeliveryCollection(shipment) ? 'TO PAY' : 'PAID';
  return raw.toUpperCase();
};


const ScanQrUpdateStatusModal = ({ isOpen, onClose, onSuccess, preferStatus = '' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const accountantAttendanceOnly = user?.role === 'Accountant';
  const isAgent = user?.role === 'Agent' || user?.role_name === 'Agent';
  const crossingAllowed = !accountantAttendanceOnly && hasShipmentCrossingAccess(user);
  const statusAllowed = !accountantAttendanceOnly && canScanQrUpdateStatus(user);
  const attendanceAllowed = canScanQrAttendance(user);
  const defaultMode = statusAllowed
    ? 'status'
    : (attendanceAllowed ? 'attendance' : 'crossing');
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);
  // 'attendance' | 'status' | 'crossing'
  const [mode, setMode] = useState(defaultMode);
  const [crossingShipments, setCrossingShipments] = useState([]);
  const [crossingFailed, setCrossingFailed] = useState([]);
  const [crossingVehicle, setCrossingVehicle] = useState(null);
  const [crossingHubCity, setCrossingHubCity] = useState(null);
  const [crossingSubmitting, setCrossingSubmitting] = useState(false);
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [attendanceMessage, setAttendanceMessage] = useState('');

  const [scanFile, setScanFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resolvedShipments, setResolvedShipments] = useState([]);
  const [resolveFailed, setResolveFailed] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [todaySheets, setTodaySheets] = useState([]);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [selectedMlSheetId, setSelectedMlSheetId] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [remarks, setRemarks] = useState('');
  const [deliveryReceiver, setDeliveryReceiver] = useState('');
  const [deliveryContact, setDeliveryContact] = useState('');
  const [deliveryImage, setDeliveryImage] = useState(null);
  const [deliveryImagePreview, setDeliveryImagePreview] = useState(null);
  const [deliveryGovIdImage, setDeliveryGovIdImage] = useState(null);
  const [deliveryGovIdPreview, setDeliveryGovIdPreview] = useState(null);
  const [otpPending, setOtpPending] = useState([]);
  const [liveScannerOpen, setLiveScannerOpen] = useState(false);
  const [liveResolving, setLiveResolving] = useState(false);
  const [keepScanningOpen, setKeepScanningOpen] = useState(false);
  const [hubAgentPick, setHubAgentPick] = useState({});
  const liveInFlightRef = useRef(false);
  const cartRef = useRef([]);
  useEffect(() => {
    cartRef.current = resolvedShipments;
  }, [resolvedShipments]);

  const resetPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const resetScanResults = () => {
    resetPreview();
    setScanFile(null);
    setResolvedShipments([]);
    setResolveFailed([]);
    setSelectedStatus('');
    setTodaySheets([]);
    setSelectedMlSheetId('');
    setServiceFilter('');
    setVehicleFilter('');
    setCrossingShipments([]);
    setCrossingFailed([]);
    setCrossingVehicle(null);
    setCrossingHubCity(null);
    if (cameraRef.current) cameraRef.current.value = '';
    if (uploadRef.current) uploadRef.current.value = '';
  };

  const resetState = () => {
    resetScanResults();
    setLiveScannerOpen(false);
    setLiveResolving(false);
    liveInFlightRef.current = false;
    setMode(defaultMode);
    setRemarks('');
    setAttendanceMessage('');
    setDeliveryReceiver('');
    setDeliveryContact('');
    setDeliveryImage(null);
    setDeliveryImagePreview(null);
    setDeliveryGovIdImage(null);
    setDeliveryGovIdPreview(null);
    setOtpPending([]);
    setKeepScanningOpen(false);
    setHubAgentPick({});
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode || updating || resolving || crossingSubmitting || attendanceSubmitting) return;
    resetScanResults();
    setRemarks('');
    setAttendanceMessage('');
    setMode(nextMode);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const noticeShipments = useMemo(
    () => resolvedShipments.filter((shipment) => shipment.notice_only),
    [resolvedShipments],
  );
  const actionableShipments = useMemo(
    () => resolvedShipments.filter((shipment) => !shipment.notice_only),
    [resolvedShipments],
  );
  const statusConfig = useMemo(
    () => getQrBatchStatusOptions(actionableShipments),
    [actionableShipments],
  );
  const allowedStatuses = statusConfig.options;

  const { eligible: eligibleShipments, skipped: skippedShipments } = useMemo(
    () => getEligibleShipmentsForQrStatus(actionableShipments, selectedStatus),
    [actionableShipments, selectedStatus],
  );

  useEffect(() => {
    if (!resolvedShipments.length) {
      setSelectedStatus('');
      return;
    }
    const hasBookedDispatchable = actionableShipments.some(
      (s) => String(s.status || '').toLowerCase() === 'booked' && s.can_dispatch !== false,
    );
    if (hasBookedDispatchable && allowedStatuses.includes('Dispatched')) {
      setSelectedStatus('Dispatched');
      return;
    }
    const wanted = String(preferStatus || '').trim();
    if (wanted && allowedStatuses.includes(wanted)) {
      setSelectedStatus(wanted);
      return;
    }
    if (statusConfig.autoSelect) {
      setSelectedStatus(statusConfig.autoSelect);
      return;
    }
    if (allowedStatuses.length > 1) {
      setSelectedStatus('');
      return;
    }
    if (allowedStatuses.length === 1) {
      setSelectedStatus(allowedStatuses[0]);
    }
  }, [
    resolvedShipments,
    actionableShipments,
    statusConfig.autoSelect,
    allowedStatuses,
    preferStatus,
  ]);

  const scannedShipmentKey = actionableShipments.map((row) => row.shipment_id).join(',');

  const suggestedServiceNumbers = useMemo(() => {
    const values = new Set();
    actionableShipments.forEach((row) => {
      const list = Array.isArray(row.service_numbers) ? row.service_numbers : [];
      list.forEach((id) => {
        const text = String(id || '').trim();
        if (text) values.add(text);
      });
      const single = String(row.service_number || '').trim();
      if (single) values.add(single);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [actionableShipments]);

  const dispatchMlLocked = Boolean(
    selectedStatus === 'Dispatched' && String(selectedMlSheetId || '').trim(),
  );

  const showReceivingAgentPicker = useMemo(
    () => shouldPickReceivingAgent(user, selectedStatus) && eligibleShipments.length > 0,
    [user, selectedStatus, eligibleShipments.length],
  );

  const receiverHubGroups = useMemo(
    () => groupShipmentsByReceiverHub(eligibleShipments),
    [eligibleShipments],
  );

  useEffect(() => {
    if (!showReceivingAgentPicker || receiverHubGroups.length === 0) {
      setHubAgentPick({});
      return undefined;
    }
    let cancelled = false;
    setHubAgentPick((prev) => {
      const next = { ...prev };
      for (const group of receiverHubGroups) {
        next[group.hubId] = {
          ...(next[group.hubId] || {}),
          label: group.label,
          count: group.shipments.length,
          loading: true,
          agents: next[group.hubId]?.agents || [],
          selectedUserId: next[group.hubId]?.selectedUserId || '',
        };
      }
      return next;
    });
    receiverHubGroups.forEach((group) => {
      shipmentService
        .getDestinationReceivingAgents(group.hubId)
        .then((res) => {
          if (cancelled) return;
          const agents = res.data?.agents || [];
          setHubAgentPick((prev) => {
            const kept = prev[group.hubId]?.selectedUserId;
            const stillValid = agents.some((a) => a.user_id === kept);
            return {
              ...prev,
              [group.hubId]: {
                label: group.label,
                count: group.shipments.length,
                loading: false,
                agents,
                selectedUserId:
                  agents.length === 1
                    ? agents[0].user_id
                    : stillValid
                      ? kept
                      : '',
              },
            };
          });
        })
        .catch(() => {
          if (cancelled) return;
          setHubAgentPick((prev) => ({
            ...prev,
            [group.hubId]: {
              label: group.label,
              count: group.shipments.length,
              loading: false,
              agents: [],
              selectedUserId: '',
            },
          }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [showReceivingAgentPicker, receiverHubGroups]);

  useEffect(() => {
    if (!scannedShipmentKey || selectedStatus !== 'Dispatched') return undefined;
    if (serviceFilter) return undefined;
    const sheetServices = todaySheets
      .map((sheet) => String(sheet.service_number || '').trim())
      .filter(Boolean);
    if (sheetServices.length === 1) {
      setServiceFilter(sheetServices[0]);
      return undefined;
    }
    const withOpenSheet = suggestedServiceNumbers.find((service) =>
      todaySheets.some(
        (sheet) => String(sheet.service_number || '').trim().toUpperCase() === service.toUpperCase(),
      ),
    );
    if (withOpenSheet) {
      setServiceFilter(withOpenSheet);
    }
    return undefined;
  }, [
    scannedShipmentKey,
    selectedStatus,
    suggestedServiceNumbers,
    todaySheets,
    serviceFilter,
  ]);

  const dispatchSenderHubId = useMemo(() => {
    const row = actionableShipments.find((s) => s.sender_location_hub_id != null);
    return row?.sender_location_hub_id ?? null;
  }, [actionableShipments]);

  const dispatchReceiverHubId = useMemo(() => {
    const row = actionableShipments.find((s) => s.receiver_location_hub_id != null);
    return row?.receiver_location_hub_id ?? null;
  }, [actionableShipments]);

  useEffect(() => {
    if (!isOpen || selectedStatus !== 'Dispatched') {
      setTodaySheets([]);
      return undefined;
    }
    let cancelled = false;
    setSheetsLoading(true);
    const params = {};
    if (dispatchSenderHubId != null && dispatchSenderHubId !== '') {
      params.sender_location_hub_id = dispatchSenderHubId;
    }
    if (dispatchReceiverHubId != null && dispatchReceiverHubId !== '') {
      params.receiver_location_hub_id = dispatchReceiverHubId;
    }
    shipmentQrStatusService.listTodayMlSheets(params)
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data || [];
        setTodaySheets(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setTodaySheets([]);
        Alert.error(err.response?.data?.error || 'Could not load ML sheets');
      })
      .finally(() => {
        if (!cancelled) setSheetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedStatus, dispatchSenderHubId, dispatchReceiverHubId]);

  /** Only services with an open same-day ML sheet (route already filtered on the API). */
  const serviceOptions = useMemo(() => {
    const values = new Set();
    todaySheets.forEach((sheet) => {
      const text = String(sheet.service_number || '').trim();
      if (text) values.add(text);
    });
    if (serviceFilter) values.add(serviceFilter);
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [todaySheets, serviceFilter]);

  const vehicleOptions = useMemo(() => {
    if (!serviceFilter) return [];
    const wanted = serviceFilter.toUpperCase();
    const values = new Set(
      todaySheets
        .filter((sheet) => String(sheet.service_number || '').trim().toUpperCase() === wanted)
        .map((sheet) => String(sheet.vehicle_number || '').trim())
        .filter(Boolean),
    );
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [todaySheets, serviceFilter]);

  const matchingSheets = useMemo(() => {
    const service = serviceFilter.trim().toUpperCase();
    const vehicle = vehicleFilter.trim().toUpperCase();
    let pool = todaySheets;
    if (service || vehicle) {
      pool = todaySheets.filter((sheet) => {
        const sheetService = String(sheet.service_number || '').trim().toUpperCase();
        const sheetVehicle = String(sheet.vehicle_number || '').trim().toUpperCase();
        if (service && sheetService !== service) return false;
        if (vehicle && sheetVehicle !== vehicle) return false;
        return true;
      });
    }
    return pool;
  }, [todaySheets, serviceFilter, vehicleFilter]);

  useEffect(() => {
    if (matchingSheets.length === 1) {
      const onlyId = matchingSheets[0].ml_sheet_id;
      setSelectedMlSheetId((current) => (current === onlyId ? current : onlyId));
      if (!serviceFilter && matchingSheets[0].service_number) {
        setServiceFilter(String(matchingSheets[0].service_number));
      }
      if (!vehicleFilter && matchingSheets[0].vehicle_number) {
        setVehicleFilter(String(matchingSheets[0].vehicle_number));
      }
      return;
    }
    setSelectedMlSheetId((current) => {
      if (!current) return current;
      const stillThere = matchingSheets.some((sheet) => sheet.ml_sheet_id === current);
      return stillThere ? current : '';
    });
  }, [matchingSheets, serviceFilter, vehicleFilter]);

  useEffect(() => {
    if (!serviceFilter || vehicleFilter) return undefined;
    if (vehicleOptions.length === 1) {
      setVehicleFilter(vehicleOptions[0]);
    }
    return undefined;
  }, [serviceFilter, vehicleOptions, vehicleFilter]);

  const handleClose = () => {
    if (updating || resolving) return;
    resetState();
    onClose?.();
  };

  const openCamera = async () => {
    if (resolving || updating || liveResolving || attendanceSubmitting) return;
    if (supportsLiveQrScan()) {
      const allowed = await ensureCameraPermission();
      if (!allowed) {
        Alert.error('Camera permission denied. Enable camera access in app settings.');
        return;
      }
      setLiveScannerOpen(true);
      return;
    }
    cameraRef.current?.click();
  };

  const handleFileSelect = async (e, { fromCamera = false } = {}) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;

    if (!isAcceptedScanFile(rawFile) && !fromCamera) {
      Alert.error('Please upload a PDF or image file (PNG, JPG, GIF, WEBP)');
      return;
    }

    try {
      setResolving(true);
      const file = await prepareQrScanImageFile(rawFile, { fromCamera });
      resetPreview();
      setScanFile(file);
      if (!isPdfFile(file)) {
        setPreviewUrl(URL.createObjectURL(file));
      }
      setResolveFailed([]);
      setCrossingShipments([]);
      setCrossingFailed([]);
      await resolveScan(file);
    } catch (err) {
      console.error('Scan file prepare failed:', err);
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 502 || status === 504 || err.code === 'ERR_NETWORK') {
        Alert.error(
          serverMsg ||
            'Upload failed (server timeout or network). Use Wi‑Fi, move closer to the QR, and try again.',
        );
      } else {
        Alert.error(
          serverMsg ||
            (fromCamera
              ? 'Could not process camera photo. Hold steady, ensure the QR is clear, and try again.'
              : 'Failed to process file. Try another image or PDF.'),
        );
      }
    } finally {
      setResolving(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const getCurrentPosition = () => getCurrentDevicePosition();

  const markAttendanceFromQr = async (qrData) => {
    const hubId = parseHubAttendanceQr(qrData);
    if (!hubId) {
      Alert.error('This is not a location hub attendance QR. Switch tab to update status or crossing.');
      return;
    }
    setAttendanceSubmitting(true);
    try {
      Alert.info('Checking your location...');
      const coords = await getCurrentPosition();
      const response = await attendanceService.qrLoginAttendance(
        qrData,
        coords.latitude,
        coords.longitude,
      );
      const message = response.message || `Attendance marked at hub ${hubId}`;
      setAttendanceMessage(message);
      Alert.success(message);
      if (onSuccess) onSuccess(response);
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Failed to mark attendance');
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  const resolveScan = async (file) => {
    try {
      if (mode === 'attendance') {
        Alert.error('Use live camera scan for the hub attendance QR.');
        return;
      }
      if (mode === 'crossing') {
        const response = await shipmentCrossingService.resolveFromFile(file);
        const data = response?.data || {};
        const shipments = data.shipments || [];
        if (!shipments.length) {
          throw new Error(response?.error || 'No shipment found in scan');
        }
        setCrossingShipments(shipments);
        setCrossingFailed(data.failed || []);
        setCrossingHubCity(data.crossing_city || null);
        const noticed = shipments.filter((shipment) => shipment.notice_only);
        if (noticed.length === shipments.length) {
          Alert.success(noticed[0].notice_message || 'Scan recorded. Crossing was not changed.');
        } else if ((data.failed || []).length > 0) {
          Alert.error(`${data.failed.length} shipment(s) could not be loaded from scan`);
        }
        return;
      }
      const response = await shipmentQrStatusService.resolveFromQr(file);
      const { shipments, failed } = normalizeQrResolveResponse(response);
      if (!shipments.length) {
        throw new Error(response?.error || 'No shipment found in scan');
      }
      await mergeScansIntoCart(shipments, failed);
      const noticed = shipments.filter((shipment) => shipment.notice_only);
      if (noticed.length === shipments.length) {
        Alert.success(noticed[0].notice_message || 'Scan recorded. Status was not changed.');
      } else if (failed.length > 0 && !shipments.some((s) => !s.notice_only)) {
        Alert.error(`${failed.length} shipment(s) could not be loaded from scan`);
      }
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 502 || status === 504 || err.code === 'ERR_NETWORK') {
        Alert.error(
          serverMsg ||
            'Could not reach the server. Check your network connection and try a smaller/clearer photo.',
        );
      } else {
        Alert.error(serverMsg || err.message || 'Could not read QR from file');
      }
      setResolvedShipments([]);
      setResolveFailed([]);
      setCrossingShipments([]);
      setCrossingFailed([]);
    } finally {
      setResolving(false);
    }
  };

  const normalizeUnitScanRow = (row) => {
    const expected = Number(row?.expected_units || row?.no_of_articles || 1) || 1;
    const units = Array.isArray(row?.units) ? row.units : [];
    const scanned = new Set();
    if (Array.isArray(row?.scanned_unit_nos)) {
      row.scanned_unit_nos.forEach((n) => {
        const num = Number(n);
        if (Number.isFinite(num) && num > 0) scanned.add(num);
      });
    }
    if (row?.scanned_unit?.unit_no != null) {
      const num = Number(row.scanned_unit.unit_no);
      if (Number.isFinite(num) && num > 0) scanned.add(num);
    }
    // Single-piece shipments: a shipment-only QR counts as unit 1.
    if (expected <= 1 && scanned.size === 0 && !row?.legacy_qr) {
      scanned.add(1);
    }
    const scanned_unit_nos = [...scanned].sort((a, b) => a - b);
    return {
      ...row,
      expected_units: expected,
      units,
      scanned_unit_nos,
      unit_scan_complete: expected <= 1 || scanned_unit_nos.length >= expected || !!row.allow_legacy_bypass,
    };
  };

  const mergeUnitIntoExisting = (existing, incoming) => {
    const base = normalizeUnitScanRow({
      ...existing,
      ...incoming,
      scanned_unit_nos: existing.scanned_unit_nos || [],
      units: incoming.units?.length ? incoming.units : existing.units,
      expected_units: incoming.expected_units || existing.expected_units,
      legacy_qr: incoming.legacy_qr || existing.legacy_qr,
      allow_legacy_bypass: existing.allow_legacy_bypass || incoming.allow_legacy_bypass,
    });
    const scanned = new Set(base.scanned_unit_nos);
    const before = scanned.size;
    if (incoming?.scanned_unit?.unit_no != null) {
      scanned.add(Number(incoming.scanned_unit.unit_no));
    }
    if (Array.isArray(incoming?.scanned_unit_nos)) {
      incoming.scanned_unit_nos.forEach((n) => scanned.add(Number(n)));
    }
    const scanned_unit_nos = [...scanned].filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
    const expected = Number(base.expected_units || 1) || 1;
    return {
      ...base,
      scanned_unit: incoming.scanned_unit || existing.scanned_unit,
      scanned_unit_nos,
      unit_scan_complete: expected <= 1 || scanned_unit_nos.length >= expected || !!base.allow_legacy_bypass,
      _unit_added: scanned_unit_nos.length > before,
      _unit_duplicate: scanned_unit_nos.length === before && !!incoming?.scanned_unit,
    };
  };

  const mergeResolvedShipments = (shipments, failed = []) => {
    setResolvedShipments((prev) => {
      const map = new Map(prev.map((s) => [s.shipment_id, s]));
      shipments.forEach((incoming) => {
        const existing = map.get(incoming.shipment_id);
        if (existing) {
          map.set(incoming.shipment_id, mergeUnitIntoExisting(existing, incoming));
        } else {
          map.set(incoming.shipment_id, normalizeUnitScanRow(incoming));
        }
      });
      const next = Array.from(map.values());
      // Keep cartRef in sync immediately so live-scan "keep open" checks see new units.
      cartRef.current = next;
      return next;
    });
    if (failed.length) {
      setResolveFailed((prev) => {
        const map = new Map(prev.map((f) => [f.shipment_id, f]));
        failed.forEach((f) => map.set(f.shipment_id, f));
        return Array.from(map.values());
      });
    }
  };

  const removeFromCart = (shipmentId) => {
    setResolvedShipments((prev) => prev.filter((s) => s.shipment_id !== shipmentId));
  };

  const toggleLegacyBypass = (shipmentId, enabled) => {
    setResolvedShipments((prev) => prev.map((row) => {
      if (row.shipment_id !== shipmentId) return row;
      return normalizeUnitScanRow({ ...row, allow_legacy_bypass: !!enabled });
    }));
  };

  const mergeScansIntoCart = async (shipments, failed = []) => {
    const actionable = shipments.filter((s) => !s.notice_only);
    const notices = shipments.filter((s) => s.notice_only);
    let toMerge = [...notices];
    const progressNotes = [];

    const mlSheetId = String(selectedMlSheetId || '').trim();
    const validateDispatch = selectedStatus === 'Dispatched' && mlSheetId;

    if (validateDispatch && actionable.length) {
      for (const row of actionable) {
        const sid = row.shipment_id;
        const existing = cartRef.current.find((s) => s.shipment_id === sid);
        if (existing) {
          const merged = mergeUnitIntoExisting(existing, row);
          toMerge.push(merged);
          if (merged._unit_duplicate) {
            progressNotes.push(`${sid}: parcel already scanned (${shipmentUnitProgressLabel(merged)})`);
          } else {
            progressNotes.push(`${sid}: ${shipmentUnitProgressLabel(merged)} parcels`);
          }
          continue;
        }
        try {
          const check = await shipmentQrStatusService.validateDispatch({
            shipment_id: sid,
            ml_sheet_id: mlSheetId,
          });
          if (check?.ok) {
            const normalized = normalizeUnitScanRow(row);
            toMerge.push(normalized);
            progressNotes.push(`${sid}: ${shipmentUnitProgressLabel(normalized)} parcels`);
          } else {
            notifyScanError(
              check?.reason || `${sid} cannot be dispatched on this route and vehicle.`,
            );
          }
        } catch (err) {
          notifyScanError(err.response?.data?.error || `${sid}: could not validate for dispatch`);
        }
      }
    } else if (actionable.length) {
      actionable.forEach((row) => {
        const existing = cartRef.current.find((s) => s.shipment_id === row.shipment_id);
        if (existing) {
          const merged = mergeUnitIntoExisting(existing, row);
          toMerge.push(merged);
          if (merged._unit_duplicate) {
            progressNotes.push(`${row.shipment_id}: parcel already scanned (${shipmentUnitProgressLabel(merged)})`);
          } else {
            progressNotes.push(`${row.shipment_id}: ${shipmentUnitProgressLabel(merged)} parcels`);
          }
          return;
        }
        const normalized = normalizeUnitScanRow(row);
        toMerge.push(normalized);
        progressNotes.push(`${row.shipment_id}: ${shipmentUnitProgressLabel(normalized)} parcels`);
      });
    }

    if (toMerge.length) {
      mergeResolvedShipments(toMerge, failed);
      if (progressNotes.length) {
        Alert.success(progressNotes.join(' · '));
      }
    } else if (failed.length) {
      mergeResolvedShipments([], failed);
    }
  };

  const mergeCrossingShipments = (shipments, failed = [], crossingCity) => {
    setCrossingShipments((prev) => {
      const map = new Map(prev.map((s) => [s.shipment_id, normalizeUnitScanRow(s)]));
      shipments.forEach((incoming) => {
        const existing = map.get(incoming.shipment_id);
        if (existing) {
          map.set(incoming.shipment_id, mergeUnitIntoExisting(existing, incoming));
        } else {
          map.set(incoming.shipment_id, normalizeUnitScanRow(incoming));
        }
      });
      return Array.from(map.values());
    });
    if (crossingCity) setCrossingHubCity(crossingCity);
    if (failed.length) {
      setCrossingFailed((prev) => {
        const map = new Map(prev.map((f) => [f.shipment_id, f]));
        failed.forEach((f) => map.set(f.shipment_id, f));
        return Array.from(map.values());
      });
    }
  };

  const openScanMore = async () => {
    setKeepScanningOpen(true);
    await openCamera();
  };

  const enrichShipmentsWithScannedUnit = (shipments, unitNo) => {
    if (unitNo == null || !Array.isArray(shipments)) return shipments || [];
    return shipments.map((row) => {
      if (row?.notice_only) return row;
      const existingNos = Array.isArray(row.scanned_unit_nos) ? row.scanned_unit_nos : [];
      const nos = [...new Set([...existingNos, Number(unitNo)].filter((n) => Number.isFinite(n) && n > 0))];
      const match = (row.units || []).find((u) => Number(u.unit_no) === Number(unitNo));
      return {
        ...row,
        scanned_unit: row.scanned_unit?.unit_no != null
          ? row.scanned_unit
          : {
              unit_no: Number(unitNo),
              article_type: match?.article_type || row.article_type || 'PACKAGE',
            },
        scanned_unit_nos: nos.length ? nos : [Number(unitNo)],
        legacy_qr: false,
      };
    });
  };

  const cartNeedsMoreParcelScans = () => {
    const rows = cartRef.current || [];
    if (!rows.length) return true;
    return rows.some((row) => !row.notice_only && !isShipmentUnitScanComplete(row));
  };

  const handleLiveQrScan = async ({ qrData, shipmentId, imageFile, unitNo: scannedUnitNo }) => {
    if (liveInFlightRef.current || liveResolving || attendanceSubmitting) return;
    liveInFlightRef.current = true;
    try {
      setLiveResolving(true);
      const hubId = parseHubAttendanceQr(qrData);
      if (hubId) {
        if (!attendanceAllowed) {
          Alert.error('QR attendance is not enabled for this company.');
          return;
        }
        if (mode !== 'attendance') setMode('attendance');
        await markAttendanceFromQr(qrData);
        return;
      }

      const resolvedShipmentId = shipmentId || parseShipmentIdFromQrData(qrData);
      const unitNo = scannedUnitNo ?? parseUnitNoFromQrData(qrData);
      // Keep camera open while multi-parcel shipments still need units.
      setKeepScanningOpen(true);
      let targetMode = mode;
      if (mode === 'attendance') {
        targetMode = statusAllowed ? 'status' : (crossingAllowed ? 'crossing' : '');
        if (!targetMode) {
          Alert.error('This is not a location hub attendance QR. Switch tab to update status or crossing.');
          return;
        }
        setMode(targetMode);
      }

      const applyCrossing = (response) => {
        const data = response?.data || response || {};
        const shipments = enrichShipmentsWithScannedUnit(data.shipments || [], unitNo);
        if (!shipments.length) {
          throw new Error(response?.error || data?.error || 'Shipment not found or access denied');
        }
        mergeCrossingShipments(shipments, data.failed || [], data.crossing_city);
        if (shipments.every((shipment) => shipment.notice_only)) {
          Alert.success(shipments[0].notice_message || 'Scan recorded. Crossing was not changed.');
        }
        if ((data.failed || []).length > 0) {
          Alert.error(`${data.failed.length} shipment(s) could not be loaded`);
        }
      };

      const applyStatus = async (response) => {
        const normalized = normalizeQrResolveResponse(response);
        const shipments = enrichShipmentsWithScannedUnit(normalized.shipments, unitNo);
        const failed = normalized.failed || [];
        if (!shipments.length) {
          throw new Error(response?.error || 'Shipment not found or access denied');
        }
        await mergeScansIntoCart(shipments, failed);
        if (shipments.every((shipment) => shipment.notice_only)) {
          Alert.success(shipments[0].notice_message || 'Scan recorded. Status was not changed.');
        }
        if (failed.length > 0 && !shipments.some((s) => !s.notice_only)) {
          Alert.error(`${failed.length} shipment(s) could not be loaded`);
        }
      };

      if (targetMode === 'crossing') {
        try {
          const response = await shipmentCrossingService.resolveFromData({
            qrData,
            shipmentId: resolvedShipmentId,
            unitNo,
          });
          applyCrossing(response);
        } catch (err) {
          if (!imageFile) throw err;
          const response = await shipmentCrossingService.resolveFromFile(imageFile);
          applyCrossing(response);
        }
        return;
      }

      try {
        const response = await shipmentQrStatusService.resolveFromQrData({
          qrData,
          shipmentId: resolvedShipmentId,
          unitNo,
        });
        await applyStatus(response);
      } catch (err) {
        if (!imageFile) throw err;
        const response = await shipmentQrStatusService.resolveFromQr(imageFile);
        await applyStatus(response);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Could not resolve scanned QR');
    } finally {
      liveInFlightRef.current = false;
      setLiveResolving(false);
      const keepOpen = cartNeedsMoreParcelScans()
        && (mode === 'status' || mode === 'crossing');
      if (keepOpen) {
        setKeepScanningOpen(true);
        setLiveScannerOpen(true);
      } else {
        setLiveScannerOpen(false);
        setKeepScanningOpen(false);
      }
    }
  };

  const handleDispatchToSheet = async (sheet) => {
    if (!sheet?.ml_sheet_id || updating) return;
    const incomplete = eligibleShipments.filter((s) => !isShipmentUnitScanComplete(s));
    if (incomplete.length) {
      Alert.error(
        `Scan all parcels before dispatch. Incomplete: ${incomplete.map((s) => `${s.shipment_id} (${shipmentUnitProgressLabel(s)})`).join(', ')}`,
      );
      return;
    }
    const ids = eligibleShipments.map((s) => s.shipment_id);
    if (!ids.length) {
      Alert.error('No booked shipment to dispatch');
      return;
    }
    try {
      setUpdating(true);
      const response = await shipmentQrStatusService.dispatchOntoMl({
        shipment_ids: ids,
        ml_sheet_id: sheet.ml_sheet_id,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(eligibleShipments),
        allow_legacy_qr: eligibleShipments.some((s) => s.allow_legacy_bypass),
      });
      if (response?.failed_count > 0) {
        const detail = (response.failed || [])
          .map((f) => `${f.shipment_id}: ${f.reason}`)
          .join('; ');
        notifyScanError(response.message || detail || 'Some shipments could not be dispatched');
        if (response.shipment_ids?.length) {
          Alert.success(`Dispatched: ${response.shipment_ids.join(', ')}`);
        }
        setResolvedShipments((prev) => prev.filter(
          (s) => !(response.shipment_ids || []).includes(s.shipment_id),
        ));
        if (onSuccess) onSuccess(response);
        return;
      }
      Alert.success(response?.message || 'Shipment dispatched');
      if (onSuccess) onSuccess(response);
      resetState();
      onClose?.();
    } catch (err) {
      notifyScanError(err.response?.data?.error || 'Failed to dispatch');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdate = async () => {
    if (!resolvedShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (!selectedStatus) {
      Alert.error('Select a status');
      return;
    }
    if (selectedStatus === 'Dispatched') {
      const sheet = todaySheets.find((row) => row.ml_sheet_id === selectedMlSheetId);
      if (!sheet) {
        Alert.error('Select an ML sheet');
        return;
      }
      await handleDispatchToSheet(sheet);
      return;
    }
    if (!allowedStatuses.includes(selectedStatus)) {
      Alert.error('Selected status is not allowed for these shipments');
      return;
    }
    if (selectedStatus === 'Delivered' && user?.delivery_details_required !== false) {
      if (!String(deliveryReceiver || '').trim()) {
        Alert.error('Receiver name is required');
        return;
      }
      if (!String(deliveryContact || '').trim()) {
        Alert.error('Receiver contact is required');
        return;
      }
      if (!deliveryImage) {
        Alert.error('Delivery photo is required');
        return;
      }
      if (!deliveryGovIdImage) {
        Alert.error('Government ID proof is required');
        return;
      }
      const unpaid = eligibleShipments
        .filter((s) => requiresDeliveryCollection(s))
        .map((s) => s.shipment_id);
      if (unpaid.length > 0) {
        Alert.error(
          `Cannot mark as Delivered until payment is complete. Unpaid: ${unpaid.join(', ')}`,
        );
        return;
      }
    }

    if (eligibleShipments.length === 0) {
      Alert.error('No shipments are eligible for this status update');
      return;
    }

    if (showReceivingAgentPicker) {
      const pickErr = validateHubAgentSelections(eligibleShipments, hubAgentPick);
      if (pickErr) {
        Alert.error(pickErr);
        return;
      }
    }

    try {
      setUpdating(true);
      const incomplete = eligibleShipments.filter((s) => !isShipmentUnitScanComplete(s));
      if (incomplete.length) {
        Alert.error(
          `Some parcels are missing. Incomplete: ${incomplete.map((s) => `${s.shipment_id} (${shipmentUnitProgressLabel(s)})`).join(', ')}`,
        );
        return;
      }
      const shipmentIds = eligibleShipments.map((s) => s.shipment_id);
      const payload = {
        shipment_ids: shipmentIds,
        status: selectedStatus,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(eligibleShipments),
        allow_legacy_qr: eligibleShipments.some((s) => s.allow_legacy_bypass) || undefined,
      };
      if (showReceivingAgentPicker) {
        payload.receiver_agent_by_shipment = buildReceiverAgentByShipment(
          eligibleShipments,
          hubAgentPick,
        );
      }
      if (remarks.trim()) payload.remarks = remarks.trim();
      if (selectedStatus === 'Delivered') {
        if (deliveryReceiver.trim()) payload.delivery_receiver = deliveryReceiver.trim();
        if (deliveryContact.trim()) payload.delivery_receiver_contact = deliveryContact.trim();
      }

      const response = await shipmentQrStatusService.updateStatusFromQr(payload, {
        delivery_receiver_image: deliveryImage,
        delivery_gov_id_image: deliveryGovIdImage,
      });

      if (isOtpRequiredResponse(response)) {
        setOtpPending(normalizeOtpPending(response));
        if (response.updated_count > 0) {
          Alert.success(response.message || 'OTP sent. Verify to complete remaining deliveries.');
        } else {
          Alert.success(response.message || 'OTP sent on WhatsApp');
        }
        return;
      }

      if (response.failed_count > 0) {
        const failedIds = (response.failed || []).map((f) => f.shipment_id).join(', ');
        Alert.error(response.message || `Some updates failed: ${failedIds}`);
      } else if (response.skipped_count > 0 && response.updated_count > 0) {
        Alert.success(response.message || 'Status updated successfully');
      } else if (response.skipped_count > 0 && !response.updated_count) {
        Alert.success(response.message || 'No changes needed');
      } else {
        Alert.success(response.message || 'Status updated successfully');
      }

      onSuccess?.(response);
      resetState();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const crossingNotices = crossingShipments.filter((s) => s.notice_only);
  const unloadCrossingShipments = crossingShipments.filter(
    (s) => s.can_unload && isShipmentUnitScanComplete(s),
  );
  const redispatchCrossingShipments = crossingShipments.filter(
    (s) => s.can_redispatch && isShipmentUnitScanComplete(s),
  );
  const canBypassLegacyQr = user?.role === 'Admin' || user?.role === 'System Admin'
    || user?.role_name === 'Admin' || user?.role_name === 'System Admin';

  const handleUnloadCrossing = async () => {
    if (!crossingShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    const incompleteUnload = crossingShipments.filter(
      (s) => s.can_unload && !isShipmentUnitScanComplete(s),
    );
    if (incompleteUnload.length) {
      Alert.error(
        `Missing parcels before unload: ${incompleteUnload.map((s) => `${s.shipment_id} (${shipmentUnitProgressLabel(s)})`).join(', ')}`,
      );
      return;
    }
    if (unloadCrossingShipments.length === 0) {
      Alert.error('No scanned shipments are ready to unload (must be Dispatched)');
      return;
    }

    try {
      setCrossingSubmitting(true);
      const response = await shipmentCrossingService.unloadForCrossing({
        shipmentIds: unloadCrossingShipments.map((s) => s.shipment_id),
        remarks: remarks.trim() || undefined,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(unloadCrossingShipments),
        allow_legacy_qr: unloadCrossingShipments.some((s) => s.allow_legacy_bypass),
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some unloads failed');
      } else {
        Alert.success(response.message || 'Shipments unloaded for crossing');
      }

      // Refresh previews so user can immediately pick vehicle and redispatch
      const refresh = await shipmentCrossingService.resolveFromData({
        shipmentIds: crossingShipments.map((s) => s.shipment_id),
      });
      const data = refresh?.data || {};
      setCrossingShipments(data.shipments || []);
      setCrossingFailed(data.failed || []);
      if (data.crossing_city) setCrossingHubCity(data.crossing_city);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to unload for crossing');
    } finally {
      setCrossingSubmitting(false);
    }
  };

  const handleCreateCrossing = async () => {
    if (!crossingShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (!crossingVehicle) {
      Alert.error('Select a vehicle for the crossing');
      return;
    }
    const incompleteRedispatch = crossingShipments.filter(
      (s) => s.can_redispatch && !isShipmentUnitScanComplete(s),
    );
    if (incompleteRedispatch.length) {
      Alert.error(
        `Missing parcels before crossing: ${incompleteRedispatch.map((s) => `${s.shipment_id} (${shipmentUnitProgressLabel(s)})`).join(', ')}`,
      );
      return;
    }
    if (redispatchCrossingShipments.length === 0) {
      Alert.error('Unload the shipment first, then dispatch onto a vehicle');
      return;
    }

    try {
      setCrossingSubmitting(true);
      const response = await shipmentCrossingService.createCrossing({
        shipmentIds: redispatchCrossingShipments.map((s) => s.shipment_id),
        newVehicleId: crossingVehicle.id,
        remarks: remarks.trim() || undefined,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(redispatchCrossingShipments),
        allow_legacy_qr: redispatchCrossingShipments.some((s) => s.allow_legacy_bypass),
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some crossings failed');
      } else {
        Alert.success(response.message || 'Crossing created successfully');
      }

      onSuccess?.(response);
      resetState();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create crossing');
    } finally {
      setCrossingSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={otpPending.length ? undefined : handleClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full min-w-0 max-w-lg max-h-[92dvh] overflow-x-hidden overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-qr-modal-title"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <h2 id="scan-qr-modal-title" className="text-lg font-bold text-gray-800 flex items-center gap-2">
              {mode === 'crossing' ? (
                <Shuffle className="w-5 h-5 text-orange-500" />
              ) : mode === 'attendance' ? (
                <CalendarCheck className="w-5 h-5 text-orange-500" />
              ) : (
                <QrCode className="w-5 h-5 text-orange-500" />
              )}
              {mode === 'crossing'
                ? 'Scan QR — Crossing'
                : mode === 'attendance'
                  ? 'Scan QR — Attendance'
                  : 'Scan QR — Update Status'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'attendance'
                ? 'Open camera, scan the printed hub QR, and keep GPS on.'
                : 'Open camera to auto-detect QR, or upload an image/PDF.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={updating || resolving || crossingSubmitting || attendanceSubmitting}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {[attendanceAllowed, statusAllowed, crossingAllowed].filter(Boolean).length > 1 && (
          <div className="px-4 sm:px-6 pt-3">
            <div className={`grid gap-1 p-1 bg-gray-100 rounded-lg ${
              [attendanceAllowed, statusAllowed, crossingAllowed].filter(Boolean).length === 3
                ? 'grid-cols-3'
                : 'grid-cols-2'
            }`}>
              {attendanceAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('attendance')}
                  className={`min-w-0 px-1 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                    mode === 'attendance' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Attendance
                </button>
              )}
              {statusAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('status')}
                  className={`min-w-0 px-1 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                    mode === 'status' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Update Status
                </button>
              )}
              {crossingAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('crossing')}
                  className={`min-w-0 px-1 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    mode === 'crossing' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  <Shuffle className="w-4 h-4" /> Crossing
                </button>
              )}
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 space-y-4">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFileSelect(e, { fromCamera: true })}
          />
          <input
            ref={uploadRef}
            type="file"
            accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp,application/pdf"
            className="hidden"
            onChange={(e) => handleFileSelect(e, { fromCamera: false })}
          />

          {mode === 'attendance' && (
            <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">
              Stand at the location hub, scan the printed attendance QR, and keep GPS on.
              You must be within 150 metres of the hub.
            </p>
          )}

          <div className={`grid gap-2 ${mode === 'attendance' ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <Button
              variant="primary"
              icon={Camera}
              fullWidth
              onClick={openCamera}
              disabled={resolving || updating || liveResolving || attendanceSubmitting}
            >
              {attendanceSubmitting ? 'Marking attendance…' : liveResolving ? 'Reading QR…' : 'Open Camera'}
            </Button>
            {mode !== 'attendance' && (
            <Button
              variant="secondary"
              icon={Upload}
              fullWidth
              onClick={() => uploadRef.current?.click()}
              disabled={resolving || updating}
            >
              Upload
            </Button>
            )}
          </div>

          {attendanceMessage && mode === 'attendance' && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              {attendanceMessage}
            </p>
          )}

          {scanFile && (
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
              {isPdfFile(scanFile) ? (
                <div className="flex items-center gap-3">
                  <FileText className="w-10 h-10 text-red-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{scanFile.name}</p>
                    <p className="text-xs text-gray-500">PDF — QR read from document pages</p>
                  </div>
                </div>
              ) : previewUrl ? (
                <img src={previewUrl} alt="QR scan preview" className="w-full max-h-40 object-contain rounded" />
              ) : null}
            </div>
          )}

          {resolving && (
            <div className="py-4">
              <Loader fullScreen={false} size="small" message="Reading QR from file..." />
            </div>
          )}

          {mode === 'status' && resolvedShipments.length > 0 && !resolving && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold text-gray-800">
                  Cart · {actionableShipments.length} shipment{actionableShipments.length === 1 ? '' : 's'}
                  {dispatchMlLocked && serviceFilter ? (
                    <span className="font-normal text-gray-500"> · {serviceFilter}</span>
                  ) : null}
                </p>
                {actionableShipments.length > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    icon={QrCode}
                    className="!py-1.5 !text-xs"
                    onClick={openScanMore}
                    disabled={resolving || updating || liveResolving}
                  >
                    Scan more
                  </Button>
                )}
              </div>

              {dispatchMlLocked && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  ML sheet locked. Further scans must belong to this service route or they will not be added.
                </p>
              )}

              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {resolvedShipments.map((shipment) => {
                  const eligibility = selectedStatus
                    ? getQrTransitionEligibility(shipment, selectedStatus)
                    : null;
                  const willUpdate = eligibility?.eligible;
                  const willSkip = eligibility && !eligibility.eligible;
                  const modeLabel = paymentModeLabel(shipment);

                  return (
                  <div
                    key={shipment.shipment_id}
                    className={`p-3 text-sm ${willUpdate ? 'bg-green-50/80' : willSkip ? 'bg-gray-50' : 'bg-orange-50/50'}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{shipment.shipment_id}</strong>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-800 bg-white border border-gray-200 rounded px-1.5 py-0.5">
                          {shipmentUnitProgressLabel(shipment)} pcs
                        </span>
                        <span className="capitalize text-gray-700">{shipment.status}</span>
                        {!shipment.notice_only && (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => removeFromCart(shipment.shipment_id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-600 mt-1 break-words">
                      {shipment.sender_location} → {shipment.receiver_location}
                    </p>
                    {shipment.scanned_unit?.article_type ? (
                      <p className="text-xs text-gray-700 mt-1">
                        Last scan: {shipment.scanned_unit.article_type}
                        {shipment.scanned_unit.unit_no != null
                          ? ` · unit ${shipment.scanned_unit.unit_no}`
                          : ''}
                      </p>
                    ) : null}
                    {!isShipmentUnitScanComplete(shipment) && !shipment.notice_only ? (
                      <p className="text-xs text-amber-800 mt-1">
                        Missing: {shipmentMissingUnitLabels(shipment).join(', ') || 'scan remaining parcel QRs'}
                      </p>
                    ) : null}
                    {shipment.legacy_qr && (shipment.expected_units || 1) > 1 && canBypassLegacyQr ? (
                      <label className="mt-1 flex items-center gap-2 text-xs text-amber-900">
                        <input
                          type="checkbox"
                          checked={!!shipment.allow_legacy_bypass}
                          onChange={(e) => toggleLegacyBypass(shipment.shipment_id, e.target.checked)}
                        />
                        Admin legacy bypass (old sticker without unit)
                      </label>
                    ) : null}
                    {shipment.legacy_qr && (shipment.expected_units || 1) > 1 && !canBypassLegacyQr ? (
                      <p className="text-xs text-amber-800 mt-1">
                        {shipment.legacy_qr_message || 'Reprint parcel stickers — this QR has no unit code.'}
                      </p>
                    ) : null}
                    <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 text-xs sm:text-sm">
                      <p className="min-w-0 break-words">
                        <span className="text-gray-500">Payment:</span>{' '}
                        <span className="font-medium text-gray-800">
                          {amountBeforeDemurrage(shipment) > 0.009
                            ? `₹${amountBeforeDemurrage(shipment).toFixed(2)}`
                            : '₹0.00'}
                          {demurrageDisplayAmount(shipment) > 0.009
                            ? ` + Demurrage ₹${demurrageDisplayAmount(shipment).toFixed(2)}`
                            : ''}
                        </span>
                      </p>
                      <p className="shrink-0 whitespace-nowrap">
                        <span className="text-gray-500">Payment mode:</span>{' '}
                        <span className={modeLabel === 'CREDIT' ? 'text-sky-800 font-medium' : modeLabel === 'TO PAY' ? 'text-red-600 font-medium' : 'text-green-700 font-medium'}>
                          {modeLabel}
                        </span>
                      </p>
                    </div>
                    {shipment.notice_only && (
                      <p className="text-xs text-amber-800 mt-1">{shipment.notice_message}</p>
                    )}
                    {selectedStatus && willSkip && !shipment.notice_only && (
                      <p className="text-xs text-amber-700 mt-1">{eligibility.reason}</p>
                    )}
                    {selectedStatus && willUpdate && (
                      <p className="text-xs text-green-700 mt-1">
                        {eligibility?.reapply
                          ? `Will confirm ${selectedStatus} again`
                          : `Will update to ${selectedStatus}`}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>

              {selectedStatus && skippedShipments.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  {skippedShipments.length} shipment(s) will be skipped for {selectedStatus}.
                  {eligibleShipments.length > 0
                    ? ` ${eligibleShipments.length} will be updated.`
                    : ' None eligible to update.'}
                </p>
              )}

              {resolveFailed.length > 0 && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium">Skipped from scan:</p>
                  {resolveFailed.map((item) => (
                    <p key={item.shipment_id}>
                      {item.shipment_id}: {item.reason}
                    </p>
                  ))}
                </div>
              )}

              {statusConfig.paymentMessage && allowedStatuses.length > 0 && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {statusConfig.paymentMessage}
                </p>
              )}

              {noticeShipments.length > 0 && actionableShipments.length === 0 ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {noticeShipments[0].notice_message || 'This scan was sent to the destination branch and admin. Status was not changed.'}
                </p>
              ) : allowedStatuses.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {statusConfig.paymentMessage || 'This shipment cannot be updated to Received or Delivered from its current status.'}
                </p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Status <span className="text-red-500">*</span>
                    </label>
                    <SelectField variant="form"
                      portal
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full min-w-0 max-w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    >
                      <option value="">Select status...</option>
                      {allowedStatuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </SelectField>
                  </div>

                  {selectedStatus === 'Dispatched' && (
                    <div className="space-y-3">
                      <div className="grid gap-2 min-w-0 grid-cols-1 sm:grid-cols-2">
                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Service Number</label>
                          <SelectField
                            variant="form"
                            portal
                            searchable
                            searchPlaceholder="Search"
                            value={serviceFilter}
                            onChange={(e) => {
                              setServiceFilter(e.target.value);
                              setVehicleFilter('');
                              setSelectedMlSheetId('');
                            }}
                            className="w-full min-w-0 max-w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                          >
                            <option value="">Select service...</option>
                            {serviceOptions.map((service) => (
                              <option key={service} value={service}>{service}</option>
                            ))}
                          </SelectField>
                          {!sheetsLoading && serviceOptions.length === 0 && (
                            <p className="text-xs text-amber-700 mt-1">
                              No open ML sheet today for a matching service. Create today&apos;s dispatch sheet for the correct service, then scan again.
                            </p>
                          )}
                          {serviceOptions.length > 1 && (
                            <p className="text-xs text-gray-600 mt-1">
                              More than one service has an open ML sheet today. Choose service and vehicle.
                            </p>
                          )}
                        </div>
                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                          <SelectField
                            variant="form"
                            portal
                            searchable={!isAgent}
                            searchPlaceholder="Search"
                            value={vehicleFilter}
                            onChange={(e) => {
                              setVehicleFilter(e.target.value);
                              setSelectedMlSheetId('');
                            }}
                            disabled={!serviceFilter || vehicleOptions.length === 0}
                            className="w-full min-w-0 max-w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                          >
                            <option value="">
                              {!serviceFilter
                                ? 'Select service first...'
                                : vehicleOptions.length
                                  ? 'Select vehicle...'
                                  : 'No vehicle for this service today'}
                            </option>
                            {vehicleOptions.map((vehicle) => (
                              <option key={vehicle} value={vehicle}>{vehicle}</option>
                            ))}
                          </SelectField>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ML Sheet <span className="text-red-500">*</span>
                        </label>
                        {sheetsLoading ? (
                          <p className="text-sm text-gray-500">Loading ML sheets...</p>
                        ) : todaySheets.length === 0 ? (
                          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            No open ML sheet for today at your branch. Create one from Dispatch, or pick service and vehicle above.
                          </p>
                        ) : matchingSheets.length === 0 ? (
                          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            No open ML sheet for this service/vehicle. Clear filters to see all of today&apos;s sheets.
                          </p>
                        ) : (
                          <SelectField
                            variant="form"
                            portal
                            value={selectedMlSheetId}
                            onChange={(e) => setSelectedMlSheetId(e.target.value)}
                            className="w-full min-w-0 max-w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                          >
                            {matchingSheets.length === 1 ? null : (
                              <option value="">Select ML sheet...</option>
                            )}
                            {matchingSheets.map((sheet) => (
                              <option
                                key={sheet.ml_sheet_id}
                                value={sheet.ml_sheet_id}
                                className="!px-2 !py-1.5 !text-xs !leading-snug !break-all !whitespace-normal"
                              >
                                {sheet.ml_sheet_id}
                                {sheet.vehicle_number ? ` · ${sheet.vehicle_number}` : ''}
                                {sheet.service_number ? ` · ${sheet.service_number}` : ''}
                              </option>
                            ))}
                          </SelectField>
                        )}
                      </div>
                      {dispatchMlLocked && (
                        <Button
                          type="button"
                          variant="secondary"
                          icon={QrCode}
                          fullWidth
                          onClick={openScanMore}
                          disabled={resolving || updating || liveResolving}
                        >
                          Scan more parcels for this dispatch
                        </Button>
                      )}
                    </div>
                  )}

                  {showReceivingAgentPicker && (
                    <HubAgentPickPanel
                      groups={receiverHubGroups}
                      hubAgentPick={hubAgentPick}
                      onSelectAgent={(hubId, userId) => {
                        setHubAgentPick((prev) => ({
                          ...prev,
                          [hubId]: { ...prev[hubId], selectedUserId: userId },
                        }));
                      }}
                    />
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={2}
                      placeholder="Add remarks for this status update..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm resize-none"
                    />
                  </div>

                  {selectedStatus === 'Delivered' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <div className="sm:col-span-2">
                        <h4 className="text-sm font-semibold text-orange-800">Delivery proof {user?.delivery_details_required === false ? '(optional)' : '(required)'}</h4>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name</label>
                        <input
                          type="text"
                          value={deliveryReceiver}
                          onChange={(e) => setDeliveryReceiver(e.target.value)}
                          placeholder="Person who received the delivery"
                          maxLength={250}
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Contact</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={deliveryContact}
                          onChange={(e) => setDeliveryContact(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="Contact number"
                          maxLength={10}
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <CapturePhotoField
                        label="Delivery photo"
                        required={user?.delivery_details_required !== false}
                        file={deliveryImage}
                        preview={deliveryImagePreview}
                        disabled={updating}
                        onPick={(file) => {
                          setDeliveryImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setDeliveryImagePreview(reader.result);
                          reader.readAsDataURL(file);
                        }}
                        onClear={() => {
                          setDeliveryImage(null);
                          setDeliveryImagePreview(null);
                        }}
                      />
                      <CapturePhotoField
                        label="Government ID proof"
                        required={user?.delivery_details_required !== false}
                        file={deliveryGovIdImage}
                        preview={deliveryGovIdPreview}
                        disabled={updating}
                        onPick={(file) => {
                          setDeliveryGovIdImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setDeliveryGovIdPreview(reader.result);
                          reader.readAsDataURL(file);
                        }}
                        onClear={() => {
                          setDeliveryGovIdImage(null);
                          setDeliveryGovIdPreview(null);
                        }}
                      />
                      </div>
                    </div>
                  )}

                  <Button
                    variant="primary"
                    icon={CheckCircle}
                    fullWidth
                    onClick={handleUpdate}
                    loading={updating}
                    disabled={
                      updating
                      || !selectedStatus
                      || eligibleShipments.length === 0
                      || (selectedStatus === 'Dispatched' && !selectedMlSheetId)
                    }
                  >
                    Update
                    {selectedStatus && eligibleShipments.length > 0
                      ? ` (${eligibleShipments.length})`
                      : ''}
                  </Button>
                </>
              )}
            </div>
          )}

          {mode === 'crossing' && crossingShipments.length > 0 && !resolving && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">
                  {crossingShipments.length > 1
                    ? `${crossingShipments.length} shipment(s) found`
                    : 'Shipment details'}
                </p>
                {crossingHubCity && (
                  <span className="text-xs text-gray-500">Crossing at: {crossingHubCity}</span>
                )}
              </div>

              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {crossingShipments.map((shipment) => (
                  <div
                    key={shipment.shipment_id}
                    className={`p-3 text-sm ${shipment.eligible ? 'bg-green-50/80' : 'bg-gray-50'}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{shipment.shipment_id}</strong>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-800 bg-white border border-gray-200 rounded px-1.5 py-0.5">
                          {shipmentUnitProgressLabel(shipment)} pcs
                        </span>
                        <span className="capitalize text-gray-700">{shipment.status}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {shipment.sender_location} → {shipment.receiver_location}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Current vehicle: {shipment.current_vehicle_number || 'N/A'}
                      {shipment.ml_sheet_id ? ` · ML: ${shipment.ml_sheet_id}` : ''}
                      {shipment.no_of_articles != null ? ` · Qty ${shipment.no_of_articles}` : ''}
                    </p>
                    {!isShipmentUnitScanComplete(shipment) && !shipment.notice_only ? (
                      <p className="text-xs text-amber-800 mt-1">
                        Missing: {shipmentMissingUnitLabels(shipment).join(', ') || 'scan remaining parcel QRs'}
                      </p>
                    ) : null}
                    {shipment.can_unload && isShipmentUnitScanComplete(shipment) && (
                      <p className="text-xs text-blue-700 mt-1">Ready to unload</p>
                    )}
                    {shipment.can_redispatch && isShipmentUnitScanComplete(shipment) && (
                      <p className="text-xs text-green-700 mt-1">Unloaded — ready to dispatch</p>
                    )}
                    {shipment.notice_only && shipment.notice_message && (
                      <p className="text-xs text-amber-800 mt-1">{shipment.notice_message}</p>
                    )}
                    {!shipment.eligible && !shipment.notice_only && shipment.reason && (
                      <p className="text-xs text-amber-700 mt-1">{shipment.reason}</p>
                    )}
                  </div>
                ))}
              </div>

              {crossingFailed.length > 0 && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium">Skipped from scan:</p>
                  {crossingFailed.map((item) => (
                    <p key={item.shipment_id}>
                      {item.shipment_id}: {item.reason}
                    </p>
                  ))}
                </div>
              )}

              {crossingNotices.length > 0 && crossingNotices.length === crossingShipments.length ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {crossingNotices[0].notice_message || 'This scan was sent to the destination branch and admin. Crossing was not changed.'}
                </p>
              ) : (
              <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={2}
                  placeholder="Add a note for unload or crossing..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm resize-none"
                />
              </div>

              <Button
                variant="secondary"
                fullWidth
                onClick={handleUnloadCrossing}
                loading={crossingSubmitting}
                disabled={crossingSubmitting || unloadCrossingShipments.length === 0}
              >
                1. Unload
                {unloadCrossingShipments.length > 0 ? ` (${unloadCrossingShipments.length})` : ''}
              </Button>

              <CrossingVehicleSelect
                value={crossingVehicle}
                onChange={setCrossingVehicle}
                disabled={crossingSubmitting}
              />

              <Button
                variant="primary"
                icon={Shuffle}
                fullWidth
                onClick={handleCreateCrossing}
                loading={crossingSubmitting}
                disabled={
                  crossingSubmitting
                  || !crossingVehicle
                  || redispatchCrossingShipments.length === 0
                }
              >
                2. Dispatch / Cross
                {redispatchCrossingShipments.length > 0
                  ? ` (${redispatchCrossingShipments.length})`
                  : ''}
              </Button>

              <p className="text-xs text-gray-400">
                Unload first, then dispatch onto a vehicle. Hub Admin or Agent at the designated
                crossing hub can complete both steps. No labour or remittance changes.
              </p>
              </>
              )}
            </div>
          )}
        </div>
      </div>

      <LiveQrScanner
        open={liveScannerOpen}
        onClose={() => {
          setLiveScannerOpen(false);
          setKeepScanningOpen(false);
        }}
        onScan={handleLiveQrScan}
        busy={liveResolving || attendanceSubmitting}
        continuous={mode === 'status' || mode === 'crossing'}
        title={mode === 'attendance' ? 'Scan Attendance QR' : 'Scan Shipment QR'}
        hint={
          mode === 'attendance'
            ? 'Hold the printed hub QR in the frame. Stay within 150m of the hub.'
            : 'Scan every parcel sticker. Camera stays open until all pieces are scanned.'
        }
      />
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={() => {
          setOtpPending([]);
          Alert.success('Delivery verified and completed');
          onSuccess?.({ success: true, otp_verified: true });
          resetState();
          onClose?.();
        }}
      />
    </div>
  );
};

export default ScanQrUpdateStatusModal;
